import { useMemo } from 'react';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  AutomationsListItemFragment,
  useGetAutomationsListQuery,
  useGetGlobalAutomationsListQuery,
  WorkflowEvent,
  WorkflowEventTriggerType,
} from 'shared/types/graphql';
import { useAppSelector } from 'state/hooks';

interface UseGetAutomationsListInput {
  inEventType?: WorkflowEvent[];
  triggerType?: WorkflowEventTriggerType;
}

interface UseGetAutomationsListResponse {
  automationsList: AutomationsListItemFragment[];
  isLoading: boolean;
}

export const useGetAutomationsList = ({
  inEventType,
  triggerType,
}: UseGetAutomationsListInput): UseGetAutomationsListResponse => {
  const { currentClinicId } = useClinicUser();
  const isGlobalEditor = useAppSelector((state) => state.automations.isGlobalEditor);

  const { data: automationsListData, loading: isLoadingAutomationsList } = useGetAutomationsListQuery({
    variables: {
      clinicId: currentClinicId || '',
      inEventType,
      triggerType,
    },
    skip: !currentClinicId || isGlobalEditor,
  });

  const { data: globalAutomationsListData, loading: isLoadingGlobalAutomationsList } = useGetGlobalAutomationsListQuery(
    {
      variables: {
        inEventType,
        triggerType,
      },
      skip: !isGlobalEditor,
    },
  );

  const automationsList = useMemo(
    () =>
      isGlobalEditor
        ? globalAutomationsListData?.findManyWorkflowEventSetting || []
        : automationsListData?.findManyWorkflowEventSetting || [],
    [automationsListData, globalAutomationsListData, isGlobalEditor],
  );

  const isLoading = useMemo(
    () => isLoadingAutomationsList || isLoadingGlobalAutomationsList,
    [isLoadingAutomationsList, isLoadingGlobalAutomationsList],
  );

  return { automationsList, isLoading };
};
